// =============================================================================
// VandieZ agent-web -- Login page (/login)
// =============================================================================
// Two-step phone-OTP login for existing agents:
//
//   Step "phone": collect phone, POST /auth/otp/request, advance to "otp".
//   Step "otp":   collect the 6-digit code, POST /auth/otp/verify.
//                 On success, store the session and redirect.
//
// New-agent signup happens via /accept (AcceptInvitationPage), not here.
// =============================================================================

import { AlertCircle, ArrowLeft, Loader2 } from "lucide-react";
import type React from "react";
import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { authApi } from "@/api/client";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { ApiError } from "@/types/auth";

type Step = "phone" | "otp";

interface LocationState {
  from?: string;
}

function normalizePhoneInput(raw: string): string {
  // Strip whitespace; users often paste with spaces.
  const trimmed = raw.replace(/\s+/g, "");
  // If they typed 10 digits and no country code, default to +91 (India).
  if (/^\d{10}$/.test(trimmed)) {
    return `+91${trimmed}`;
  }
  return trimmed;
}

export function LoginPage(): React.JSX.Element {
  const { user, setSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const redirectTarget = (location.state as LocationState | null)?.from ?? "/";

  const [step, setStep] = useState<Step>("phone");
  const [phone, setPhone] = useState("");
  const [otpSessionId, setOtpSessionId] = useState<string | null>(null);
  const [maskedPhone, setMaskedPhone] = useState<string | null>(null);
  const [otpCode, setOtpCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function callVerify(): Promise<void> {
    if (otpSessionId === null) {
      setError("Session expired -- request a new code.");
      setStep("phone");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const response = await authApi.verifyOtp({
        otp_session_id: otpSessionId,
        otp_code: otpCode,
      });
      setSession({
        user: response.user,
        accessToken: response.access_token,
        refreshToken: response.refresh_token,
      });
      navigate(redirectTarget, { replace: true });
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  // Already logged in? Don't show the form -- bounce straight in.
  if (user !== null) {
    return <Navigate to={redirectTarget} replace />;
  }

  async function handleRequestOtp(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    const normalized = normalizePhoneInput(phone);
    if (!/^\+\d{10,15}$/.test(normalized)) {
      setError("Enter a valid phone number with country code (e.g., +91XXXXXXXXXX).");
      return;
    }

    setSubmitting(true);
    try {
      const response = await authApi.requestOtp({
        phone: normalized,
        purpose: "login",
      });
      setOtpSessionId(response.otp_session_id);
      setMaskedPhone(response.masked_phone);
      setPhone(normalized);
      setStep("otp");
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleVerifyOtp(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!/^\d{4,8}$/.test(otpCode)) {
      setError("Enter the OTP code (digits only).");
      return;
    }
    await callVerify();
  }

  function handleBackToPhone(): void {
    setStep("phone");
    setOtpCode("");
    setOtpSessionId(null);
    setMaskedPhone(null);
    setError(null);
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-2">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">
            VandieZ
          </p>
          <CardTitle>Agent sign in</CardTitle>
          <CardDescription>
            {step === "phone"
              ? "Enter your phone number; we will text you a one-time code."
              : `Enter the 6-digit code sent to ${maskedPhone ?? "your phone"}.`}
          </CardDescription>
        </CardHeader>

        {step === "phone" ? (
          <form onSubmit={handleRequestOtp}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone number</Label>
                <Input
                  id="phone"
                  type="tel"
                  autoComplete="tel"
                  inputMode="tel"
                  placeholder="+91XXXXXXXXXX"
                  value={phone}
                  onChange={(e) => { setPhone(e.target.value); }}
                  required
                  disabled={submitting}
                />
                <p className="text-xs text-muted-foreground">
                  10-digit Indian numbers are auto-prefixed with +91.
                </p>
              </div>

              {error !== null ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              ) : null}
            </CardContent>

            <CardFooter>
              <Button type="submit" disabled={submitting} className="w-full">
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending code...
                  </>
                ) : (
                  "Send code"
                )}
              </Button>
            </CardFooter>
          </form>
        ) : (
          <form onSubmit={handleVerifyOtp}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="otp">One-time code</Label>
                <Input
                  id="otp"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="\d*"
                  maxLength={8}
                  placeholder="123456"
                  value={otpCode}
                  onChange={(e) => { setOtpCode(e.target.value.replace(/\D/g, "")); }}
                  required
                  autoFocus
                  disabled={submitting}
                />
              </div>

              {error !== null ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              ) : null}
            </CardContent>

            <CardFooter className="flex flex-col gap-2">
              <Button type="submit" disabled={submitting} className="w-full">
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  "Sign in"
                )}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={handleBackToPhone}
                disabled={submitting}
                className="w-full"
              >
                <ArrowLeft className="h-4 w-4" />
                Use a different number
              </Button>
            </CardFooter>
          </form>
        )}
      </Card>
    </div>
  );
}

function extractErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    // For VALIDATION_ERROR, the first errors[] entry is usually the most
    // actionable.
    if (err.problem.errors !== undefined && err.problem.errors.length > 0) {
      const first = err.problem.errors[0];
      if (first !== undefined) {
        return first.msg;
      }
    }
    return err.problem.detail;
  }
  if (err instanceof Error) {return err.message;}
  return "Something went wrong. Please try again.";
}
